import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, ConciergeBell, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, cx, Badge, Button, buttonClass, Breadcrumb, Section } from "@/components/ui";
import {
  type BulkCheckResponse,
  type SearchResponse,
  cartTermLabel,
  domainKeys,
  domainsApi,
  hasPromotion,
  termOptions,
  useCart,
  type Cart,
  type CartItem,
  type PillTone,
  type SearchResult,
} from "@/lib/domains";
import { formatMoney } from "@/lib/format";

import {
  ConciergeModal,
  ConciergePill,
  DomainRequestsPanel,
  defaultConciergeMessage,
  useDomainRequests,
} from "./purchase/Concierge";
import { nameInitials, reasonOf } from "./purchase/helpers";
import { TransferModal } from "./TransferModal";
import { NoVatNote, OfferPill, UsualPrice } from "./purchase/Pricing";
import { DomainIcon, Heading, HeadTile, DomainPage, TwoColumns, Notice } from "@/components/domains/parts";

/** Alternatives shown at first, and added by each "Show more". */
const RECOMMENDATIONS_PER_PAGE = 5;
/** Gap between the starts of the alternatives one "Show more" reveals, so they arrive one by one. */
const REVEAL_STAGGER_MS = 90;

/**
 * Figma "Find a domain" (4073-5929): search every registrar at once and pick names. The cart
 * summary and the transfer-in entry (modal 4090-9998) are additions the product needs.
 */
export function FindDomainPage() {
  const { orgId, current, can } = useOrganization();
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const [text, setText] = useState(q);
  const cart = useCart();
  const manage = can("domains:manage");

  const [shownQ, setShownQ] = useState(q);
  if (shownQ !== q) {
    setShownQ(q);
    setText(q);
  }
  const bulkMode = params.get("mode") === "bulk";
  const setBulkMode = (on: boolean) =>
    setParams(
      (p) => {
        if (on) p.set("mode", "bulk");
        else p.delete("mode");
        return p;
      },
      { replace: true },
    );
  // Each search starts the results afresh (back to the first five alternatives), even the same name again.
  const [round, setRound] = useState(0);

  const search = useQuery({
    queryKey: ["domains", orgId, "search", q],
    queryFn: () => domainsApi.search(orgId, q),
    enabled: q.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const transferOpen = params.get("transfer") === "1";
  const setTransferOpen = (open: boolean) =>
    setParams(
      (p) => {
        if (open) p.set("transfer", "1");
        else {
          p.delete("transfer");
          p.delete("fqdn");
        }
        return p;
      },
      { replace: true },
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim().toLowerCase();
    if (!value) return;
    setRound((n) => n + 1);
    setParams((p) => {
      p.set("q", value);
      return p;
    });
    if (value === q) void search.refetch();
  };

  const items = cart.data?.items ?? [];
  const requests = useDomainRequests();
  const hasRequests = (requests.data?.requests ?? []).some((r) => r.status !== "cancelled");
  const side =
    items.length > 0 || hasRequests ? (
      <>
        {items.length > 0 && cart.data && <CartSummary cart={cart.data} />}
        <DomainRequestsPanel />
      </>
    ) : undefined;

  return (
    <DomainPage>
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Find domain" }]} />
      <Heading
        size={24}
        tile={<HeadTile>{nameInitials(current.organization.name)}</HeadTile>}
        title="Find a domain"
        subtitle="Search available domains and connect one to your website"
      />

      <div role="tablist" aria-label="How to search" className="flex gap-1.5">
        {[
          { on: false, label: "One name" },
          { on: true, label: "Several names" },
        ].map((tab) => (
          <Button
            unstyled
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={bulkMode === tab.on}
            onClick={() => setBulkMode(tab.on)}
            className={cx(
              "rounded-full px-3.5 py-1.5 text-[13px] font-semibold",
              bulkMode === tab.on ? "bg-[#1a1513] text-[#fef8f6]" : "text-[#5c5551] hover:bg-[#f3ebe7]",
            )}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {bulkMode ? (
        <BulkSearch cart={cart.data} />
      ) : (
        <form
          onSubmit={submit}
          role="search"
          className="flex items-center gap-2 rounded-xl border border-border bg-surface py-1.5 pr-1.5 pl-4 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20"
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Search for a name, like yourbusiness.com"
            aria-label="Search for a domain"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
            autoFocus={!q}
            autoComplete="off"
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent py-1.5 text-[15px] text-fg outline-none! placeholder:text-fg-subtle"
          />
          <Button
            variant="brand"
            size="lg"
            type="submit"
            disabledReason={text.trim() ? undefined : "Type a name to search for"}
            loading={search.isFetching}
            className="px-4"
          >
            Search
          </Button>
        </form>
      )}

      {!manage && (
        <p className="text-sm text-fg-subtle">
          You can search, but only people who manage domains can buy them. Ask the business owner.
        </p>
      )}

      <TwoColumns
        main={
          <>
            {!bulkMode && q && search.data && (
              <RegistrarNotice
                registrars={search.data.registrars}
                results={search.data.results}
                primary={search.data.primary}
              />
            )}
            {bulkMode ? null : q ? (
              <Results
                title={`Results for "${q}"`}
                searchId={search.data?.search_id ?? null}
                key={`${q}#${round}`}
                loading={search.isPending}
                error={search.error}
                primary={search.data?.primary ?? null}
                recommendations={search.data?.recommendations ?? []}
                currency={search.data?.currency ?? cart.data?.currency ?? "GBP"}
                cart={cart.data}
                onRetry={() => void search.refetch()}
              />
            ) : (
              <SearchTips />
            )}
            <Button
              unstyled
              requires="domains:manage"
              onClick={() => setTransferOpen(true)}
              className="flex items-center gap-3.5 rounded-2xl border border-dashed border-border bg-surface p-[18px] text-left hover:bg-[#fdfbfa]"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[#faf6ef]">
                <ArrowRightLeft className="size-[18px] text-[#5c5551]" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-fg">Transfer a domain you own elsewhere</span>
                <span className="text-[13px] text-fg-subtle">
                  Move it to CortexGrip with the authorization code from your current registrar
                </span>
              </span>
              <span className="text-[13px] font-semibold text-brand-500">Start →</span>
            </Button>
            <p className="text-[13px] text-fg-subtle">
              Moving several domains?{" "}
              <Link to="/domains/transfer/bulk" className="font-semibold text-brand-500 hover:underline">
                Transfer up to 100 at once
              </Link>
            </p>
          </>
        }
        side={side}
      />

      {items.length > 0 && cart.data && <MobileCartBar cart={cart.data} />}

      {manage && (
        <TransferModal
          key={params.get("fqdn") ?? ""}
          open={transferOpen}
          initialFqdn={params.get("fqdn") ?? ""}
          onClose={() => setTransferOpen(false)}
        />
      )}
    </DomainPage>
  );
}

function SearchTips() {
  return (
    <Section title="Search tips" subtitle="Type the name you want — with or without an ending">
      <p className="text-sm text-[#5c5551]">
        We check the exact name you type, then suggest available alternatives in other endings and close
        variations. Prices shown are per year, excluding VAT. To check a list, use{" "}
        <span className="font-semibold">Several names</span>.
      </p>
      <p className="text-sm text-[#5c5551]">{NO_RESERVATION}</p>
    </Section>
  );
}

function statusPill(r: SearchResult): { label: string; tone: PillTone } {
  if (r.launch_phase === "early_access" && r.status === "available")
    return { label: "Early access", tone: "warning" };
  if (r.premium && r.status === "available") return { label: "Premium", tone: "warning" };
  switch (r.status) {
    case "available":
      return r.suggested && !r.exact
        ? { label: "Alternative", tone: "success" }
        : { label: "Available", tone: "success" };
    case "premium":
      return { label: "Premium", tone: "warning" };
    case "taken":
      return { label: "Taken", tone: "danger" };
    case "yours":
      return { label: "Yours", tone: "info" };
    case "unsupported":
      return { label: "Not offered", tone: "neutral" };
    default:
      return { label: "Couldn't check", tone: "neutral" };
  }
}

/** Said wherever a result could be mistaken for a hold on the name. */
const NO_RESERVATION =
  "Search results don't reserve a domain: anyone can register it until your order goes through. We check availability again at checkout and right before registering.";

function Results({
  title,
  searchId,
  loading,
  error,
  primary = null,
  recommendations = [],
  results,
  currency,
  cart,
  onRetry,
}: {
  title: string;
  searchId: string | null;
  loading: boolean;
  error: unknown;
  primary?: SearchResult | null;
  recommendations?: SearchResult[];
  /** Several names checked at once: one flat list, in the order given, instead of a name and its alternatives. */
  results?: SearchResult[];
  currency: string;
  cart: Cart | undefined;
  onRetry: () => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [failed, setFailed] = useState<{ fqdn: string; error: unknown } | null>(null);
  const [asking, setAsking] = useState<SearchResult | null>(null);
  const setCart = (next: Cart) => qc.setQueryData(domainKeys.cart(orgId), next);

  const add = useMutation({
    mutationFn: (fqdn: string) => domainsApi.addToCart(orgId, { fqdn, search_id: searchId }),
    onMutate: () => setFailed(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) }),
    onError: (err, fqdn) => setFailed({ fqdn, error: err }),
  });
  const remove = useMutation({
    mutationFn: (itemId: string) => domainsApi.removeFromCart(orgId, itemId),
    onSuccess: setCart,
  });

  const [shown, setShown] = useState(RECOMMENDATIONS_PER_PAGE);
  const visible = recommendations.slice(0, shown);
  const inCart = (fqdn: string) => cart?.items.find((i) => i.fqdn === fqdn);

  // "Show more" hands focus to the first name it revealed (the button may vanish), and brings it into view.
  const [revealedFrom, setRevealedFrom] = useState<number | null>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (revealedFrom === null) return;
    const first = list.current?.children[revealedFrom];
    if (!(first instanceof HTMLElement)) return;
    first.focus({ preventScroll: true });
    first.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [revealedFrom]);

  /** `recommendation`: the row's place among the alternatives; those "Show more" adds fade in one by one. */
  const row = (r: SearchResult, first: boolean, recommendation?: { index: number }) => {
    const revealed = recommendation !== undefined && recommendation.index >= RECOMMENDATIONS_PER_PAGE;
    const pill = statusPill(r);
    const concierge = r.restricted;
    const offered = !concierge && (r.status === "available" || r.status === "premium");
    // Shown with its price, but it can't be bought online right now (stale price, premium quote…).
    const sunrise = offered && (r.sunrise_only || r.launch_phase === "sunrise");
    const blocked = offered && !sunrise && r.blocked_reason !== null;
    const buyable = offered && !blocked && !sunrise;
    const item = inCart(r.fqdn);
    return (
      <div
        key={r.fqdn}
        tabIndex={recommendation ? -1 : undefined}
        style={
          revealed
            ? { animationDelay: `${(recommendation.index % RECOMMENDATIONS_PER_PAGE) * REVEAL_STAGGER_MS}ms` }
            : undefined
        }
        className={cx(
          "flex flex-col gap-2 py-3.5",
          !first && "border-t border-border",
          revealed && "animate-reveal motion-reduce:animate-none",
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p
              className={cx(
                "truncate text-[15px] font-semibold",
                offered || concierge || r.status === "yours" ? "text-fg" : "text-fg-subtle",
              )}
            >
              {r.display_name}
            </p>
            <p className="text-[12.5px] text-fg-subtle">{rowDetail(r, currency)}</p>
            {blocked && (
              <p id={blockedId(r)} className="text-[12.5px] text-[#853f00]">
                {r.blocked_message ?? "This name can't be bought online right now."}
              </p>
            )}
          </div>
          {concierge ? (
            <ConciergePill requested={!!r.request_number} />
          ) : (
            <Badge tone={pill.tone} size="sm">
              {pill.label}
            </Badge>
          )}
          {concierge &&
            (r.request_number ? (
              <span className="inline-flex items-center rounded-[9px] bg-[#f3ebe7] px-3 py-[9px] font-mono text-xs font-semibold text-[#5c5551]">
                {r.request_number}
              </span>
            ) : (
              <Button
                unstyled
                requires="domains:manage"
                onClick={() => setAsking(r)}
                className="inline-flex items-center justify-center gap-1.5 rounded-[9px] bg-[#1a1513] px-3.5 py-[9px] text-sm font-semibold whitespace-nowrap text-[#fef8f6] shadow-[0_3px_5px_rgba(26,20,18,0.25)] transition-colors hover:bg-[#2a2320]"
              >
                <ConciergeBell className="size-4 text-[#f8d79a]" />
                Contact us
              </Button>
            ))}
          {r.status === "yours" && (
            <Link
              to={r.domain_id ? `/domains/${r.domain_id}` : `/domains?q=${encodeURIComponent(r.fqdn)}`}
              className={buttonClass("secondary", "lg")}
            >
              Manage
            </Link>
          )}
          {sunrise && (
            <Link
              to={`/domains/sunrise/apply?fqdn=${encodeURIComponent(r.fqdn)}`}
              className={buttonClass("brand", "lg")}
            >
              Apply
            </Link>
          )}
          {blocked && (
            <Button
              variant="brand"
              size="lg"
              disabledReason={r.blocked_message ?? "This name can't be bought online right now."}
            >
              Unavailable
            </Button>
          )}
          {buyable &&
            (item ? (
              <span className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-[9px] bg-[#eef8f3] px-3.5 py-[9px] text-sm font-semibold text-[#007955]">
                  Added ✓
                </span>
                <Button
                  unstyled
                  onClick={() => remove.mutate(item.id)}
                  loading={remove.isPending}
                  className="text-[13px] font-semibold text-fg-subtle hover:text-fg disabled:opacity-50"
                >
                  Remove
                </Button>
              </span>
            ) : (
              <Button
                variant="brand"
                size="lg"
                onClick={() => add.mutate(r.fqdn)}
                requires="domains:manage"
                loading={add.isPending && add.variables === r.fqdn}
                disabledReason={add.isPending ? "Adding another name to your cart…" : undefined}
              >
                Select
              </Button>
            ))}
        </div>
        {failed?.fqdn === r.fqdn && (
          <ErrorAlert
            error={
              reasonOf(failed.error) === "DOMAIN_TAKEN"
                ? new Error(`${r.display_name} was just taken by someone else. Pick another name.`)
                : failed.error
            }
          />
        )}
      </div>
    );
  };

  return (
    <section className="flex flex-col rounded-2xl border border-border bg-surface px-[22px] pt-5 pb-2 shadow-[0_6px_10px_rgba(26,20,18,0.08)]">
      <h2 className="pb-2 text-lg leading-[22px] font-semibold text-fg">{title}</h2>

      {loading ? (
        <div className="flex flex-col" aria-live="polite">
          <div className="flex items-center gap-2.5 py-3">
            <DomainIcon name="loading" size={18} className="animate-[spin_2.4s_linear_infinite]" />
            <p className="text-sm text-fg-subtle">Checking every registrar — this can take a few seconds…</p>
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 border-t border-border py-3.5">
              <div className="flex flex-1 flex-col gap-2">
                <span className="h-3.5 w-40 animate-pulse rounded bg-[#f3ebe7]" />
                <span className="h-3 w-56 animate-pulse rounded bg-[#faf6ef]" />
              </div>
              <span className="h-[30px] w-16 animate-pulse rounded-[9px] bg-[#faf6ef]" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col gap-3 pb-4">
          <ErrorAlert error={error} />
          <Button size="lg" variant="secondary" className="self-start" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : results ? (
        <div className="flex flex-col">
          {results.map((r, i) => row(r, i === 0))}
          <ErrorAlert error={remove.error} />
          <p className="border-t border-border py-3 text-xs text-fg-subtle">
            Prices exclude VAT. {NO_RESERVATION}
          </p>
        </div>
      ) : !primary ? (
        <p className="pb-4 text-sm text-fg-subtle">
          Nothing to show for this name. Check the spelling, or try a shorter name.
        </p>
      ) : (
        <div className="flex flex-col">
          {row(primary, true)}
          <section aria-labelledby="recommendations" className="flex flex-col border-t border-border pt-4">
            <h3
              id="recommendations"
              className="text-[11.5px] font-semibold tracking-[0.575px] text-fg-subtle uppercase"
            >
              Recommended alternatives
            </h3>
            {visible.length === 0 ? (
              <p className="py-3.5 text-sm text-fg-subtle">No available recommendations found</p>
            ) : (
              <div ref={list} className="flex flex-col">
                {visible.map((r, i) => row(r, i === 0, { index: i }))}
              </div>
            )}
            {recommendations.length > visible.length && (
              <Button
                size="lg"
                variant="secondary"
                className="mb-3.5 self-center"
                onClick={() => {
                  setRevealedFrom(visible.length);
                  setShown((n) => n + RECOMMENDATIONS_PER_PAGE);
                }}
              >
                Show more
              </Button>
            )}
          </section>
          <ErrorAlert error={remove.error} />
          <p className="border-t border-border py-3 text-xs text-fg-subtle">
            Prices exclude VAT. {NO_RESERVATION}
          </p>
        </div>
      )}
      <ConciergeModal result={asking} onClose={() => setAsking(null)} />
    </section>
  );
}

/** The id of a blocked row's reason, for the disabled button's description. */
const blockedId = (r: SearchResult) => `blocked-${r.fqdn.replace(/[^a-z0-9-]/gi, "-")}`;

function rowDetail(r: SearchResult, currency: string): ReactNode {
  if (r.restricted) {
    if (r.request_number) return `Request ${r.request_number} sent — our team will be in touch`;
    const message = r.restriction_message ?? defaultConciergeMessage(r.fqdn.split(".").slice(1).join("."));
    return r.register_cents !== null
      ? `${message} Guide price ${formatMoney(r.register_cents, currency)} first year.`
      : message;
  }
  switch (r.status) {
    case "taken":
      return "Unavailable — registered by another owner";
    case "yours":
      return "Already in your workspace";
    case "unsupported":
      return "CortexGrip can't register this ending yet";
    case "unknown":
      return "Its registry didn't answer — search again in a minute";
    default: {
      if (r.launch_phase === "sunrise" || r.sunrise_only) {
        return "Sunrise: applications only";
      }
      if (r.register_cents === null) {
        return r.status === "premium" || r.blocked_reason === "PREMIUM_QUOTE_REQUIRED"
          ? "Premium · price on request"
          : "Price unavailable right now";
      }
      const promo = hasPromotion(r);
      // Renewals are never discounted: with an offer, fall back to the usual price, not the offer.
      const renewCents = r.renew_cents ?? (promo ? r.standard_register_cents : null);
      const min = r.min_years > 1 ? ` · minimum ${r.min_years} years` : "";
      if (r.launch_phase === "early_access" && r.early_access_fee_cents !== null) {
        const total = r.early_access_total_cents ?? r.register_cents + r.early_access_fee_cents;
        return (
          <>
            <span className="block">
              Early access fee today {formatMoney(r.early_access_fee_cents, currency)}
            </span>
            <span className="block">Standard price {formatMoney(r.register_cents, currency)} first year</span>
            <span className="block font-semibold">Total first year {formatMoney(total, currency)}</span>
            {renewCents !== null && (
              <span className="block">
                Then {formatMoney(renewCents, currency)}/year{min}
              </span>
            )}
          </>
        );
      }
      if (r.premium && renewCents !== null) {
        return (
          <>
            {formatMoney(r.register_cents, currency)} first year · renews at{" "}
            {formatMoney(renewCents, currency)}/year
            {min}
          </>
        );
      }
      return (
        <>
          {formatMoney(r.register_cents, currency)}
          {promo && r.standard_register_cents !== null && (
            <>
              {" "}
              <UsualPrice cents={r.standard_register_cents} currency={currency} />
            </>
          )}{" "}
          first year
          {renewCents !== null && ` · then ${formatMoney(renewCents, currency)}/year`}
          {min}
          {promo && (
            <span className="ml-1.5 inline-flex align-middle">
              <OfferPill promotion={r} />
            </span>
          )}
        </>
      );
    }
  }
}

// ── several names ───────────────────────────────────────────────────────────
/** Check a pasted list at once: duplicates are checked once, entries that aren't names say why. */
function BulkSearch({ cart }: { cart: Cart | undefined }) {
  const { orgId } = useOrganization();
  const [text, setText] = useState("");
  const [checked, setChecked] = useState<BulkCheckResponse | null>(null);
  const check = useMutation({
    mutationFn: (names: string[]) => domainsApi.bulkCheck(orgId, names),
    onSuccess: setChecked,
  });
  const entries = text.split(/[\s,;]+/).filter(Boolean);

  return (
    <>
      <form
        className="flex flex-col gap-2 rounded-xl border border-border bg-surface p-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20"
        onSubmit={(e) => {
          e.preventDefault();
          if (entries.length > 0) check.mutate(entries);
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={"saltandbloom.com\nsaltandbloom.co.uk\nbloomflorist.shop"}
          aria-label="Domain names to check, one per line"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
          autoFocus
          spellCheck={false}
          className="min-h-28 resize-y bg-transparent px-1 py-1 font-mono text-[14px] text-fg outline-none placeholder:text-fg-subtle"
        />
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex-1 text-[12.5px] text-fg-subtle">
            One per line, or separated by commas — each with its ending. Up to {checked?.limit ?? 20} names at
            a time; repeats are checked once.
          </p>
          <Button
            variant="brand"
            size="lg"
            type="submit"
            disabledReason={entries.length === 0 ? "Enter at least one domain name" : undefined}
            loading={check.isPending}
            className="px-4"
          >
            Check {entries.length > 1 ? `${entries.length} names` : "names"}
          </Button>
        </div>
      </form>
      {check.error ? <ErrorAlert error={check.error} /> : null}
      {checked && (checked.invalid.length > 0 || checked.duplicates.length > 0) && (
        <Notice
          tone="warning"
          title={
            checked.invalid.length > 0
              ? `${checked.invalid.length} ${checked.invalid.length === 1 ? "entry isn't" : "entries aren't"} a domain name`
              : "Repeats were checked once"
          }
        >
          {checked.invalid.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {checked.invalid.map((i) => (
                <li key={i.entry}>
                  <span className="font-mono font-semibold">{i.entry}</span> — {i.message}
                </li>
              ))}
            </ul>
          )}
          {checked.duplicates.length > 0 && ` Skipped as repeats: ${checked.duplicates.join(", ")}.`}
        </Notice>
      )}
      {checked && checked.results.length > 0 && (
        <>
          <RegistrarNotice registrars={checked.registrars} results={checked.results} primary={null} />
          <Results
            title={`${checked.results.length} ${checked.results.length === 1 ? "name" : "names"} checked`}
            searchId={checked.search_id}
            loading={false}
            error={null}
            results={checked.results}
            currency={checked.currency}
            cart={cart}
            onRetry={() => check.mutate(entries)}
          />
        </>
      )}
    </>
  );
}

// ── cart ────────────────────────────────────────────────────────────────────
function CartSummary({ cart }: { cart: Cart }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const setCart = (next: Cart) => qc.setQueryData(domainKeys.cart(orgId), next);
  const years = useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => domainsApi.setCartYears(orgId, id, n),
    onSuccess: setCart,
  });
  const remove = useMutation({
    mutationFn: (id: string) => domainsApi.removeFromCart(orgId, id),
    onSuccess: setCart,
  });
  const count = cart.items.length;

  return (
    <Section title="Your cart" subtitle={`${count} ${count === 1 ? "domain" : "domains"}`}>
      <div className="flex flex-col">
        {cart.items.map((item, i) => (
          <CartLine
            key={item.id}
            item={item}
            currency={cart.currency}
            first={i === 0}
            busy={years.isPending || remove.isPending}
            onYears={(n) => years.mutate({ id: item.id, n })}
            onRemove={() => remove.mutate(item.id)}
          />
        ))}
      </div>
      <div className="flex flex-col gap-1.5 border-t border-border pt-3.5 text-sm">
        <div className="flex justify-between text-[#5c5551]">
          <span>Subtotal</span>
          <span>{formatMoney(cart.net_cents, cart.currency)}</span>
        </div>
        <div className="flex justify-between text-[#5c5551]">
          <span>VAT</span>
          <span>{formatMoney(cart.vat_cents, cart.currency)}</span>
        </div>
        <NoVatNote items={cart.items} />
        <div className="flex justify-between text-base font-semibold text-fg">
          <span>Total</span>
          <span>{formatMoney(cart.total_cents, cart.currency)}</span>
        </div>
      </div>
      <ErrorAlert error={years.error ?? remove.error} />
      <Link to="/domains/checkout" className={buttonClass("brand", "lg", "w-full")}>
        Continue to checkout
      </Link>
    </Section>
  );
}

function CartLine({
  item,
  currency,
  first,
  busy,
  onYears,
  onRemove,
}: {
  item: CartItem;
  currency: string;
  first: boolean;
  busy: boolean;
  onYears: (n: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className={cx("flex items-start gap-2.5 py-3", !first && "border-t border-border")}>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="truncate text-sm font-semibold text-fg">{item.fqdn}</p>
        {item.kind === "transfer" ? (
          <p className="text-xs text-fg-subtle">Transfer · includes 1 year</p>
        ) : (
          <label className="flex items-center gap-1.5 text-xs text-fg-subtle">
            <span className="sr-only">Years for {item.fqdn}</span>
            <select
              value={item.years}
              disabled={busy}
              onChange={(e) => onYears(Number(e.target.value))}
              className="rounded-md border border-border bg-surface px-1.5 py-1 text-xs text-fg"
            >
              {termOptions(item.periods, item.years).map((n) => (
                <option key={n} value={n}>
                  {cartTermLabel(item, n, currency)}
                </option>
              ))}
            </select>
            {item.premium && <span className="font-semibold text-[#853f00]">Premium</span>}
          </label>
        )}
        {item.kind === "register" && hasPromotion(item) && (
          <p className="text-xs text-fg-subtle">
            First-year offer
            {item.standard_register_cents !== null && (
              <>
                {" "}
                (<UsualPrice cents={item.standard_register_cents} currency={currency} />{" "}
                {formatMoney(item.register_cents, currency)})
              </>
            )}{" "}
            · then{" "}
            {formatMoney(item.renew_cents ?? item.standard_register_cents ?? item.register_cents, currency)}
            /year
          </p>
        )}
      </div>
      <span className="pt-0.5 text-sm font-semibold text-fg">{formatMoney(item.net_cents, currency)}</span>
      <Button
        unstyled
        type="button"
        onClick={onRemove}
        disabled={busy}
        aria-busy={busy}
        aria-label={`Remove ${item.fqdn} from cart`}
        className="flex size-6 items-center justify-center rounded-md text-fg-subtle hover:bg-muted hover:text-fg disabled:opacity-50"
      >
        <X className="size-3.5" />
      </Button>
    </div>
  );
}

/** Phones and tablets: the cart column sits far below the results, so keep a checkout bar in reach. */
function MobileCartBar({ cart }: { cart: Cart }) {
  const count = cart.items.length;
  return (
    <div className="sticky bottom-4 z-10 flex items-center gap-3 rounded-2xl border border-border bg-surface p-3 pl-4 shadow-[0_10px_20px_rgba(26,20,18,0.16)] xl:hidden">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">
          {count} {count === 1 ? "domain" : "domains"} in cart
        </p>
        <p className="text-xs text-fg-subtle">{formatMoney(cart.total_cents, cart.currency)} incl. VAT</p>
      </div>
      <Link to="/domains/checkout" className={buttonClass("brand", "lg")}>
        Checkout
      </Link>
    </div>
  );
}

/** Says when answers didn't come from a real registry: test mode, or every registrar unreachable. */
function RegistrarNotice({
  registrars,
  results,
  primary,
}: {
  registrars: SearchResponse["registrars"];
  results: SearchResponse["results"];
  primary: SearchResponse["primary"];
}) {
  if (registrars.some((r) => r.provider === "simulated")) {
    return (
      <Notice tone="warning" title="Test mode — availability is simulated">
        These results come from CortexGrip's test registrar, not a real domain registry, so "Available" and
        "Taken" are made up. Nothing bought here is really registered.
      </Notice>
    );
  }
  if (registrars.length > 0 && registrars.every((r) => r.status !== "ok")) {
    return (
      <Notice tone="danger" title="We couldn't reach the domain registry">
        Availability can't be checked right now, so nothing is shown as available. Try again in a minute.
      </Notice>
    );
  }
  if (primary?.status === "unknown") {
    return (
      <Notice tone="warning" title={`The registry couldn't check ${primary.display_name}`}>
        Its registry didn't answer just now, so it's shown as "Couldn't check" rather than guessed. Search
        again in a minute.
      </Notice>
    );
  }
  const unchecked = results.filter((r) => r.status === "unknown").length;
  if (unchecked > 0) {
    return (
      <Notice
        tone="warning"
        title={`The registry couldn't check ${unchecked} ${unchecked === 1 ? "alternative" : "alternatives"}`}
      >
        Their registry didn't answer just now, so they're left out of the recommendations rather than guessed.
        Search again in a minute.
      </Notice>
    );
  }
  return null;
}
